import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { PublicLayout } from "../../components/layout/PublicLayout";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { ArrowRight, MapPin, Briefcase, Clock, TrendingUp, ShieldCheck, Sparkles } from "lucide-react";

const HERO_IMG = "/kera-hero.jpg";

const VALUES = [
  { icon: ShieldCheck, title: "AUTHENTICITY", desc: "We represent food, culture and relationships with integrity — rooted in authentic South Indian heritage." },
  { icon: TrendingUp, title: "GROWTH", desc: "We are building a stronger retail and distribution network across Ireland and Europe." },
  { icon: Sparkles, title: "OPPORTUNITY", desc: "Join a growing food brand where commercial talent can make a visible impact." },
];

export default function Home() {
  const [jobs, setJobs] = useState([]);
  const navigate = useNavigate();

  useEffect(() => { api.get("/jobs").then(({ data }) => setJobs(data)).catch(() => {}); }, []);

  return (
    <PublicLayout>
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#101820] text-[#FAF6EE]">
        <div className="absolute inset-0 kera-pattern opacity-40" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 relative">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#C79A3B] font-semibold mb-5">
              <span className="w-8 h-px bg-[#C79A3B]" /> KERA · Ireland
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05]">
              BUILD THE NEXT<br /><span className="text-[#C79A3B]">CHAPTER OF KERA</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-[#FAF6EE]/75 max-w-2xl leading-relaxed">
              We're growing across Ireland and Europe — and we're looking for ambitious people who know how to build markets, relationships and lasting customer partnerships.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <Button data-testid="hero-view-roles-button" onClick={() => document.querySelector("#roles")?.scrollIntoView({ behavior: "smooth" })}
                className="bg-[#C79A3B] hover:bg-[#D4AF57] text-[#101820] font-bold rounded-full px-7 h-12 text-base shadow-lg shadow-[#C79A3B]/25 transition-all hover:-translate-y-0.5">
                View Open Roles <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button data-testid="hero-create-profile-button" variant="outline" onClick={() => navigate("/register")}
                className="border-[#C79A3B]/60 bg-transparent text-[#FAF6EE] hover:bg-[#C79A3B]/10 hover:text-[#FAF6EE] rounded-full px-7 h-12 text-base">
                Create Candidate Profile
              </Button>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }} className="mt-12">
            <div className="rounded-2xl overflow-hidden border border-[#C79A3B]/40 shadow-2xl">
              <img src={HERO_IMG} alt="KERA — Favourite dishes of Kerala" className="w-full h-auto block" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* VALUES */}
      <section id="values" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-[#101820]">Why build your career at KERA</h2>
          <p className="mt-3 text-muted-foreground">More than a food brand — a commercial growth platform for people who love winning markets.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {VALUES.map((v, i) => (
            <motion.div key={v.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              data-testid={`value-card-${v.title.toLowerCase()}`}
              className="bg-white rounded-xl border border-[#C79A3B]/30 p-7 shadow-md hover:border-[#C79A3B] hover:-translate-y-1 transition-all duration-300">
              <div className="w-12 h-12 rounded-lg bg-[#101820] grid place-items-center mb-5">
                <v.icon className="text-[#C79A3B] h-6 w-6" />
              </div>
              <h3 className="font-heading font-bold text-lg tracking-wide text-[#101820]">{v.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{v.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ROLES */}
      <section id="roles" className="bg-[#FAF6EE] border-y border-[#C79A3B]/20 py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
            <div>
              <span className="text-xs uppercase tracking-[0.25em] text-[#C79A3B] font-semibold">Open Roles</span>
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-[#101820] mt-2">Current Vacancies</h2>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {jobs.map((job) => (
              <div key={job.id} data-testid={`vacancy-card-${job.slug}`}
                className="bg-white rounded-xl border border-[#C79A3B]/30 p-6 shadow-md hover:border-[#C79A3B] hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-[10px] uppercase tracking-wider bg-[#C79A3B]/15 text-[#8a6a1e] px-2 py-1 rounded-full font-semibold">{job.category}</span>
                  <span className="text-[10px] uppercase tracking-wider bg-[#101820]/5 text-[#101820] px-2 py-1 rounded-full font-semibold">{job.level}</span>
                </div>
                <h3 className="font-heading text-xl font-bold text-[#101820]">{job.title}</h3>
                <div className="mt-4 space-y-2 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-[#C79A3B]" /> {job.location_short}</div>
                  <div className="flex items-center gap-2"><Briefcase className="h-4 w-4 text-[#C79A3B]" /> {job.employment_type} · {job.work_style}</div>
                  <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-[#C79A3B]" /> {job.applications_count} applicant(s)</div>
                </div>
                <p className="mt-3 text-sm text-[#101820]/70 font-medium">{job.salary_display}</p>
                <Button data-testid={`view-role-${job.slug}`} onClick={() => navigate(`/jobs/${job.slug}`)}
                  className="mt-5 bg-[#101820] hover:bg-[#1A242F] text-[#FAF6EE] rounded-full">
                  View Role <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            ))}
            {jobs.length === 0 && <p className="text-muted-foreground">No open roles right now — check back soon.</p>}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
